// app/actions/registration.ts
"use server";

import { createHmac, randomInt, timingSafeEqual } from "crypto";
import { supabaseAdmin } from "@/lib/supabaseAdmin";
import { sendVerificationCodeEmail } from "@/lib/email";
import { cookies } from "next/headers";
import { LOCALE_COOKIE } from "@/lib/i18n/config";
import type { Locale } from "@/lib/i18n/config";

async function getLocaleFromCookies(): Promise<Locale | undefined> {
  try {
    const store = await cookies();
    return (store.get(LOCALE_COOKIE)?.value as Locale) || undefined;
  } catch {
    return undefined;
  }
}
import { validateUsername } from "@/lib/username-validation";

export type RegistrationDataInput = {
  firstName: string;
  lastName:  string;
  username:  string;
  password:  string;
  email:     string;
};

export type RegistrationResponse = {
  errors?: Record<string,string>;
  success?: string;
};

const OTP_TTL_MINUTES = 15;

function validatePassword(p: string): boolean {
  return (
    p.length >= 12 &&
    /[A-Z]/.test(p) &&
    /[!@#$%^&*()\[\],.?":{}|<>\/\\\-_]/.test(p)
  );
}

function hashOtp(otp: string): string {
  const secret = process.env.REGISTRATION_OTP_SECRET ||
    (process.env.NODE_ENV === "production" ? undefined : "dev-registration-otp");
  if (!secret) {
    throw new Error("REGISTRATION_OTP_SECRET is required in production");
  }
  return createHmac("sha256", secret).update(otp.trim()).digest("hex");
}

function verifyOtpHash(otp: string, storedHash: string): boolean {
  const h = hashOtp(otp);
  if (h.length !== storedHash.length) return false;
  try {
    return timingSafeEqual(Buffer.from(h), Buffer.from(storedHash));
  } catch {
    return false;
  }
}

export async function registerAction(
  dataInput: RegistrationDataInput
): Promise<RegistrationResponse> {
  const { firstName, lastName, username, password, email } = dataInput;
  const errors: Record<string,string> = {};

  if (!firstName) errors.firstName = "First name is required";
  if (!lastName)  errors.lastName  = "Last name is required";
  if (!username)  errors.username  = "Username is required";
  if (!password)  errors.password  = "Password is required";
  if (!email)     errors.email     = "Email is required";
  if (Object.keys(errors).length) return { errors };

  if (!validatePassword(password)) {
    return {
      errors: {
        password:
          "Password must be 12+ chars, one uppercase & one special char.",
      },
    };
  }

  if (username.includes("@")) {
    return {
      errors: {
        username: "Username cannot be an email address",
      },
    };
  }

  const usernameValidation = validateUsername(username);
  if (!usernameValidation.valid) {
    return { errors: { username: usernameValidation.error } };
  }

  const normUser  = username.toLowerCase();
  const normEmail = email.toLowerCase();

  const { data: existing, error: chkErr } = await supabaseAdmin
    .from("profiles")
    .select("username,email,banned_at")
    .or(
      `username.ilike.${normUser},email.eq.${normEmail}`
    );
  if (chkErr) {
    return { errors: { general: chkErr.message } };
  }
  if (existing?.length) {
    for (const p of existing) {
      const isBanned = Boolean((p as { banned_at?: string | null }).banned_at);
      if (p.username?.toLowerCase() === normUser) {
        if (isBanned) return { errors: { general: "This account has been suspended." } };
        errors.username = "Username already taken";
      }
      if (p.email === normEmail) {
        if (isBanned) return { errors: { general: "This account has been suspended." } };
        errors.email = "Email already taken";
      }
    }
    return { errors };
  }

  const otp = String(randomInt(100000, 999999));
  const expiresAt = new Date(Date.now() + OTP_TTL_MINUTES * 60 * 1000);

  const { error: upsertErr } = await supabaseAdmin
    .from("pending_registrations")
    .upsert(
      {
        email: normEmail,
        otp_hash: hashOtp(otp),
        first_name: firstName,
        last_name: lastName,
        username,
        expires_at: expiresAt.toISOString(),
      },
      { onConflict: "email" }
    );

  if (upsertErr) {
    return { errors: { general: upsertErr.message } };
  }

  try {
    const locale = await getLocaleFromCookies();
    await sendVerificationCodeEmail({
      to: normEmail,
      code: otp,
      userName: username,
      locale,
    });
  } catch (e) {
    console.error("sendVerificationCodeEmail failed:", e);
    return { errors: { general: "Failed to send verification email." } };
  }

  return {
    success: "Check your email for the verification code.",
  };
}

export type ConfirmRegistrationInput = {
  email: string;
  otp: string;
  password: string;
  firstName: string;
  lastName: string;
  username: string;
};

export type ConfirmRegistrationResponse = {
  error?: string;
  success?: boolean;
};

export async function confirmRegistrationAction(
  input: ConfirmRegistrationInput
): Promise<ConfirmRegistrationResponse> {
  const { email, otp, password, firstName, lastName, username } = input;
  const normEmail = email.toLowerCase().trim();

  if (!normEmail || !otp || !password || !firstName || !lastName || !username) {
    return { error: "Missing required fields." };
  }

  const { data: pending, error: fetchErr } = await supabaseAdmin
    .from("pending_registrations")
    .select("otp_hash, first_name, last_name, username, expires_at")
    .eq("email", normEmail)
    .maybeSingle();

  if (fetchErr) {
    return { error: fetchErr.message };
  }
  if (!pending) {
    return { error: "Invalid or expired verification code." };
  }

  if (new Date(pending.expires_at) < new Date()) {
    await supabaseAdmin.from("pending_registrations").delete().eq("email", normEmail);
    return { error: "Verification code has expired. Please request a new one." };
  }

  if (!verifyOtpHash(otp, pending.otp_hash)) {
    return { error: "Invalid verification code." };
  }

  const { error: createErr } = await supabaseAdmin.auth.admin.createUser({
    email: normEmail,
    password,
    email_confirm: true,
    user_metadata: {
      firstName: pending.first_name,
      lastName: pending.last_name,
      username: pending.username,
    },
  });

  if (createErr) {
    return { error: createErr.message };
  }

  await supabaseAdmin.from("pending_registrations").delete().eq("email", normEmail);

  return { success: true };
}

export async function resendRegistrationOtpAction(email: string): Promise<RegistrationResponse> {
  const normEmail = email?.toLowerCase().trim();
  if (!normEmail) {
    return { errors: { general: "Email is required." } };
  }

  const { data: pending, error: fetchErr } = await supabaseAdmin
    .from("pending_registrations")
    .select("first_name, last_name, username")
    .eq("email", normEmail)
    .maybeSingle();

  if (fetchErr || !pending) {
    return { errors: { general: "No pending registration found. Please start over." } };
  }

  const otp = String(randomInt(100000, 999999));
  const expiresAt = new Date(Date.now() + OTP_TTL_MINUTES * 60 * 1000);

  const { error: updateErr } = await supabaseAdmin
    .from("pending_registrations")
    .update({
      otp_hash: hashOtp(otp),
      expires_at: expiresAt.toISOString(),
    })
    .eq("email", normEmail);

  if (updateErr) {
    return { errors: { general: updateErr.message } };
  }

  try {
    await sendVerificationCodeEmail({
      to: normEmail,
      code: otp,
      userName: pending.username,
    });
  } catch (e) {
    console.error("resendRegistrationOtp sendVerificationCodeEmail failed:", e);
    return { errors: { general: "Failed to send verification email." } };
  }

  return { success: "A new code has been sent to your email." };
}
